import { useMutation } from '@tanstack/react-query'
import { DoorOpen, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { posSessionService, type CashCapacity } from '../../../services/pos.service'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../ui/dialog'
import { Input } from '../../ui/input'

interface SessionRequestError {
  response?: {
    data?: {
      message?: string
    }
  }
}

interface OpenRegisterSessionModalProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  onReleaseRegister?: () => void
  onSuccess: (sessionData: unknown) => void
  cashCapacity: CashCapacity | null
}

export function OpenRegisterSessionModal({ open, onOpenChange, onReleaseRegister, onSuccess, cashCapacity }: OpenRegisterSessionModalProps) {
  const navigate = useNavigate()
  const [notes, setNotes] = useState('')
  const [denominations, setDenominations] = useState<Record<number, number>>({})

  // Compute initialBalance from denominations
  const computedBalance = useMemo(() => {
    return Object.entries(denominations).reduce(
      (total, [denom, count]) => total + parseFloat(denom) * count,
      0
    )
  }, [denominations])

  const cashLimit = Number(cashCapacity?.limit_amount || 0)
  const willExceedCashLimit = cashLimit > 0 && computedBalance > cashLimit

  const handleDenominationChange = (denom: number, count: number) => {
    setDenominations((prev) => ({
      ...prev,
      [denom]: Math.max(0, count),
    }))
  }

  const openSessionMutation = useMutation({
    mutationFn: (payload: { denominations: { denomination: number; quantity: number }[]; notes?: string }) =>
      posSessionService.createSession(payload),
    onSuccess: (data) => {
      toast.success('Sesión de caja abierta exitosamente')
      setDenominations({})
      setNotes('')
      onSuccess(data)
    },
    onError: (error: unknown) => {
      const msg = (error as SessionRequestError)?.response?.data?.message
        ?? 'No se pudo abrir la sesión. Intenta nuevamente.'
      toast.error(msg)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const denominationsPayload = Object.entries(denominations)
      .filter(([, count]) => count > 0)
      .map(([denom, count]) => ({
        denomination: parseFloat(denom),
        quantity: count,
      }))

    openSessionMutation.mutate({ 
      denominations: denominationsPayload,
      notes: notes.trim() || undefined 
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange || (() => {})}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-primary">
            <DoorOpen className="h-6 w-6" />
            Apertura de Caja
          </DialogTitle>
          <DialogDescription className="text-sm text-table-foreground pt-2 pb-4">
            Has asignado la caja correctamente. Puedes registrar las denominaciones del fondo inicial o abrir la sesión con $0.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="rounded-lg border border-primary/20 bg-primary/10 p-4 mb-4">
              <p className="mb-2 text-sm font-medium text-primary/90">
                Calculadora de Denominaciones:
              </p>
              <p className="text-xs text-primary">
                Ingresa la cantidad de monedas y billetes disponibles. Si no hay fondo inicial, deja las cantidades vacías.
              </p>
            </div>

            {/* Tabla de denominaciones */}
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground uppercase">Denominación</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground uppercase">Cantidad</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[1000, 500, 200, 100, 50, 20, 10, 5, 2, 1].map((denom) => (
                    <tr key={denom} className="transition-colors hover:bg-table-row-hover">
                      <td className="px-4 py-2 font-medium text-table-foreground">
                        ${denom}
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min="0"
                          value={denominations[denom] || ''}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            handleDenominationChange(
                              denom,
                              isNaN(val) ? 0 : Math.max(0, val)
                            );
                          }}
                          onFocus={(e) => e.target.select()}
                          className="h-8 w-24 text-center font-medium"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 font-semibold text-table-foreground">
                        ${(denom * (denominations[denom] || 0)).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 pt-4 border-t">
              <label className="text-sm font-medium text-table-foreground block mb-2">Fondo inicial de caja (Calculado)</label>
              <Input
                type="text"
                value={`$${computedBalance.toFixed(2)}`}
                readOnly
                className="text-lg font-bold bg-card text-card-foreground border-border"
              />
              {cashLimit > 0 && (
                <p className={`mt-1 text-xs ${willExceedCashLimit ? 'text-amber-700' : 'text-table-foreground'}`}>
                  {willExceedCashLimit
                    ? 'El fondo inicial superará el límite y la caja no aceptará nuevas entradas hasta realizar un retiro.'
                    : `Límite de la caja: $${cashLimit.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`}
                </p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium text-table-foreground block mb-2">Notas (Opcional)</label>
              <Input
                value={notes}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)}
                placeholder="Ej. Billetes de 20 y monedas"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:w-full">
            {onReleaseRegister && (
              <Button
                type="button"
                variant="ghost"
                className="w-full sm:w-auto h-11 text-red-600 hover:text-red-700 hover:bg-red-50 sm:mr-auto"
                onClick={onReleaseRegister}
              >
                Liberar Caja
              </Button>
            )}
            <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto h-11"
                onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="w-full sm:w-auto h-11 text-base bg-primary hover:bg-primary/90 flex-1 sm:flex-none"
                disabled={openSessionMutation.isPending}
              >
                {openSessionMutation.isPending ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <DoorOpen className="mr-2 h-5 w-5" />
                )}
                Abrir Sesión
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
